/**
 * DatePickerDialog Component
 *
 * A reusable date picker dialog with calendar UI
 * Supports RTL layout and Gregorian calendar
 */

import React, {useState, useMemo} from 'react';
import Modal from './Modal';
import {Calendar, ChevronLeft, ChevronRight} from 'lucide-react';
import {formatDate} from '../../utils/dateUtils';

// Helper function to format date as YYYY-MM-DD
const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface DatePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (date: string) => void;
  selectedDate?: string;
  minDate?: string;
  maxDate?: string;
  title?: string;
}

const DatePickerDialog: React.FC<DatePickerDialogProps> = ({
  open,
  onClose,
  onSelect,
  selectedDate,
  minDate,
  maxDate,
  title = 'اختر التاريخ'
}) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (selectedDate) {
      const date = new Date(selectedDate);
      return new Date(date.getFullYear(), date.getMonth(), 1);
    }
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  // Get first day of month and number of days
  const calendarData = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday, 6 = Saturday

    // Arabic day names (starting from Sunday)
    const dayNames = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

    // Arabic month names
    const monthNames = [
      'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
      'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
    ];

    return {
      year,
      month,
      monthName: monthNames[month],
      daysInMonth,
      startingDayOfWeek,
      dayNames
    };
  }, [currentMonth]);

  // Generate array of days for the calendar
  const daysArray = useMemo(() => {
    const {daysInMonth, startingDayOfWeek} = calendarData;
    const days: (number | null)[] = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  }, [calendarData]);

  // Navigation functions
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  // Check if a date is selectable
  const isDateSelectable = (day: number): boolean => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateString = formatDateForInput(date);

    if (minDate && dateString < minDate) {return false;}
    if (maxDate && dateString > maxDate) {return false;}

    return true;
  };


  // Check if a day is selected
  const isSelected = (day: number): boolean => {
    if (!selectedDate) {return false;}
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return formatDateForInput(date) === selectedDate;
  };

  // Check if a day is today
  const isToday = (day: number): boolean => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return formatDateForInput(date) === formatDateForInput(today);
  };

  // Handle day selection
  const handleDayClick = (day: number) => {
    if (!isDateSelectable(day)) {return;}
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateString = formatDateForInput(date);
    onSelect(dateString);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={title} width="max-w-md">
      <div className="space-y-6">
        {/* Month Navigation */}
        <div className="flex items-center justify-between px-2">
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 text-gray-600 hover:text-gray-900"
            aria-label="الشهر السابق"
            type="button"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="text-center">
            <h3 className="text-xl font-bold font-zain text-gray-900">
              {calendarData.monthName} {calendarData.year}
            </h3>
            <button
              onClick={goToToday}
              className="text-sm font-zain font-medium text-primary hover:text-primary/80 mt-2 transition-colors duration-200"
              type="button"
            >
              اليوم
            </button>
          </div>

          <button
            onClick={goToNextMonth}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 text-gray-600 hover:text-gray-900"
            aria-label="الشهر التالي"
            type="button"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2">
          {/* Day Headers */}
          {calendarData.dayNames.map((dayName, index) => (
            <div
              key={index}
              className="text-center py-3 text-sm font-bold font-zain text-gray-700"
            >
              {dayName}
            </div>
          ))}

          {/* Calendar Days */}
          {daysArray.map((day, index) => {
            if (day === null) {
              return <div key={index} />;
            }

            const selectable = isDateSelectable(day);
            const selected = isSelected(day);
            const todayClass = isToday(day);

            return (
              <button
                key={index}
                onClick={() => handleDayClick(day)}
                disabled={!selectable}
                className={`
                  aspect-square flex items-center justify-center rounded-lg text-sm font-zain font-medium
                  transition-all duration-200
                  ${!selectable
                    ? 'text-gray-300 cursor-not-allowed'
                    : selected
                    ? 'bg-primary text-white hover:bg-primary/90 shadow-md'
                    : todayClass
                    ? 'bg-primary/10 text-primary border-2 border-primary/30 hover:bg-primary/20 font-bold'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }
                `}
                type="button"
              >
                {day}
              </button>
            );
          })}
        </div>

        {/* Selected Date Display */}
        {selectedDate && (
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-center gap-2 text-sm font-zain text-gray-600 bg-gray-50 rounded-lg py-2 px-4">
              <Calendar className="w-4 h-4 text-primary" />
              <span className="font-medium">التاريخ المحدد:</span>
              <span className="font-bold text-gray-900">{formatDate(selectedDate)}</span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DatePickerDialog;

